import { Link } from 'react-router-dom';
import { DT, DS2 } from '../../theme/designTokens';
import { useIsMobile } from '../../hooks/useBreakpoint';
import { useTranslation } from '../../i18n/react';
import { ASSETS } from '../../config/assets';

/**
 * Column config holds TRANSLATION KEYS (ns-prefixed, resolved with t() at
 * render) for active destinations, and LITERAL English labels for the known
 * dead links (/about, /faq, /help, /privacy, /terms — routes that don't
 * exist and fall through to the catch-all redirect). Dead links are
 * deliberately NOT translated, removed, or repaired during localization;
 * they remain a separately-tracked navigation issue.
 */
type FooterLink = { to: string; key: string } | { to: string; literal: string };

const COLUMNS: ReadonlyArray<{ headingKey: string; links: readonly FooterLink[] }> = [
  { headingKey: 'navigation:footer.solutions', links: [
    { key: 'navigation:items.playOnline', to: '/play/online' },
    { key: 'navigation:items.playBots',   to: '/play/ai' },
    { key: 'navigation:items.leaderboard', to: '/leaderboard' },
    { key: 'navigation:items.friends',     to: '/friends' },
  ]},
  { headingKey: 'navigation:footer.support', links: [
    { key: 'navigation:items.rules',       to: '/rules' },
    { literal: 'Who we are?',        to: '/about' },
    { literal: 'F.A.Q.',             to: '/faq' },
    { literal: 'Contact us / Support', to: '/help' },
    // Real, working route (unlike the dead links above) — App Store
    // Guideline 5.1.1(v) / Google Play Account Deletion policy require a
    // web-reachable path to this that doesn't require installing the app.
    { key: 'navigation:items.deleteAccount', to: '/delete-account' },
  ]},
  { headingKey: 'navigation:footer.account', links: [
    { key: 'common:action.signUp', to: '/signup' },
    { key: 'common:action.logIn',  to: '/login' },
  ]},
];

/**
 * Design System V2 — shared marketing footer.
 * Matches the approved Figma footer, reused identically on Landing and
 * Leaderboard screenshots — a genuine app-wide component, not Landing-only.
 */
export function Footer() {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  return (
    // marginTop:'auto' — sticks Footer flush to the bottom of its flex-column
    // screen root when content is shorter than the viewport (see AppShell.tsx
    // + each screen's root div comment); no-op on tall/scrolling pages, where
    // Footer already sits right after the content that pushed the page past
    // viewport height.
    <footer style={{ background: DS2.color.surface, borderTop: '1px solid #CB9719', padding: isMobile ? `24px 20px calc(16px + ${DT.layout.safeAreaBottom})` : `37px 35px calc(24px + ${DT.layout.safeAreaBottom})`, marginTop: 'auto', flexShrink: 0 }}>
      <div
        style={{
          maxWidth:       DS2.layout.contentMaxWidth,
          margin:         '0 auto',
          display:        'flex',
          flexDirection:  isMobile ? 'column' : 'row',
          flexWrap:       'wrap',
          justifyContent: 'space-between',
          gap:            isMobile ? 20 : 32,
        }}
      >
        {/* Logo — approved Figma footer lockup (334.8×145.2 reference, wide) */}
        <img
          src={ASSETS.brand.LOGO_FOOTER}
          alt="The Domino Game"
          style={{ width: isMobile ? 96 : 120, height: 'auto', objectFit: 'contain', flexShrink: 0 }}
          draggable={false}
        />

        {/* Link columns */}
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: isMobile ? '14px 20px' : 24 }}>
          {COLUMNS.map((col, i) => (
            <div
              key={col.headingKey}
              style={
                isMobile
                  ? { flex: '1 1 40%', minWidth: 120 }
                  : i > 0 ? { paddingLeft: 24, borderLeft: '1px solid rgba(203,151,25,0.35)' } : undefined
              }
            >
              <div style={{ fontSize: isMobile ? 13.5 : `clamp(1rem, ${DS2.type.footerHeading.pref}vw, ${DS2.type.footerHeading.max}px)`, fontWeight: 700, fontFamily: DS2.font.heading, color: '#FFFFFF', letterSpacing: '-0.03em', marginBottom: isMobile ? 8 : 18 }}>
                {t(col.headingKey as never)}
              </div>
              {/* Row pitch comes from Figma's 217% link line-height, not a flex gap */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {col.links.map(l => (
                  <Link
                    key={l.to}
                    to={l.to}
                    style={{ fontSize: isMobile ? 11.5 : `clamp(0.8rem, ${DS2.type.footerLink.pref}vw, ${DS2.type.footerLink.max}px)`, lineHeight: isMobile ? 1.7 : 1.86, fontFamily: DS2.font.small, color: '#FFFFFF', textDecoration: 'none' }}
                  >
                    {'literal' in l ? l.literal : t(l.key as never)}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Social icons — approved Figma export (53×53 RGBA source) */}
        <div style={{ display: 'flex', gap: isMobile ? 14 : 15, alignItems: 'flex-start' }}>
          <SocialIcon label="Facebook" src={ASSETS.social.FACEBOOK} size={isMobile ? 17 : 19} />
          <SocialIcon label="Instagram" src={ASSETS.social.INSTAGRAM} size={isMobile ? 17 : 19} />
          <SocialIcon label="YouTube" src={ASSETS.social.YOUTUBE} size={isMobile ? 17 : 19} />
          <SocialIcon label="TikTok" src={ASSETS.social.TIKTOK} size={isMobile ? 17 : 19} />
        </div>
      </div>

      {/* Legal row */}
      <div
        style={{
          maxWidth:       DS2.layout.contentMaxWidth,
          margin:         isMobile ? '16px auto 0' : '24px auto 0',
          paddingTop:     isMobile ? 12 : 19,
          borderTop:      `1px solid ${DT.color.border}`,
          display:        'flex',
          flexWrap:       'wrap',
          justifyContent: 'space-between',
          gap:            isMobile ? 8 : 13,
        }}
      >
        <div style={{ display: 'flex', gap: isMobile ? 16 : 20 }}>
          <Link to="/privacy" style={{ fontSize: isMobile ? 10.5 : 11.4, fontFamily: DS2.font.small, color: '#FFFFFF', textDecoration: 'none' }}>Privacy policy</Link>
          <Link to="/terms" style={{ fontSize: isMobile ? 10.5 : 11.4, fontFamily: DS2.font.small, color: '#FFFFFF', textDecoration: 'none' }}>Terms of service</Link>
        </div>
        <span style={{ fontSize: isMobile ? 11.5 : 13.5, fontFamily: DS2.font.small, color: '#FFFFFF' }}>
          © {new Date().getFullYear()} The Domino Game.
        </span>
      </div>
    </footer>
  );
}

function SocialIcon({ label, src, size = 20 }: { label: string; src: string; size?: number }) {
  return (
    <div aria-label={label} title={label} style={{ width: size, height: size }}>
      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable={false} />
    </div>
  );
}
